import { useEffect, useRef, useState } from 'react'
import type { EmblaCarouselType } from 'embla-carousel'

/**
 * Buffers the total slide count while Embla is mid-animation so no
 * DOM nodes are added/removed during a scroll. Card content within
 * existing slots updates immediately — only structural changes (new
 * slides) are deferred until settle.
 */
const useBufferedSlideCount = (
  count: number,
  emblaApi: EmblaCarouselType | undefined,
): number => {
  const [rendered, setRendered] = useState(count)
  const pendingRef = useRef<number | null>(null)
  const scrollingRef = useRef(false)

  useEffect(() => {
    if (!emblaApi) return undefined

    const onScroll = () => {
      scrollingRef.current = true
    }

    const onSettle = () => {
      scrollingRef.current = false
      if (pendingRef.current !== null) {
        setRendered(pendingRef.current)
        pendingRef.current = null
      }
    }

    emblaApi.on('scroll', onScroll)
    emblaApi.on('settle', onSettle)
    emblaApi.on('reInit', onSettle)

    return () => {
      emblaApi.off('scroll', onScroll)
      emblaApi.off('settle', onSettle)
      emblaApi.off('reInit', onSettle)
    }
  }, [emblaApi])

  useEffect(() => {
    if (scrollingRef.current) {
      pendingRef.current = count
    } else {
      setRendered(count)
      pendingRef.current = null
    }
  }, [count])

  return rendered
}

export default useBufferedSlideCount
